import { Immutable } from "immer";
import React from "react";

import type { ProductDescriptionBlock as Value } from "@reactivated";

// NOTE: This component reproduces the legacy server-rendered markup from
// `cms/landing_page.html` (the `product_description_block` branch) verbatim —
// every classname is load-bearing for the existing SCSS, so keep them in sync.
// The `landing-page-description__button button button--ming ` class string
// intentionally carries the legacy trailing space (the LifestyleModule
// precedent matches legacy raw class strings; whitespace is render-irrelevant
// in the browser).
type IProps = Immutable<{
    value: Value;
}>;

const backgroundImageStyle = (
    image: Immutable<Value["image"]>,
): React.CSSProperties | undefined => {
    // Legacy emitted the SAME image url twice in the background-image shorthand
    // (`url(X), url(X)`). When the image is null the legacy markup rendered
    // `url(), url()` (broken urls); we instead omit the inline style entirely
    // while keeping the wrapper element (LifestyleModule precedent).
    if (!image) {
        return undefined;
    }
    return { backgroundImage: `url(${image.url}), url(${image.url})` };
};

export const LandingProductDescriptionBlock = (
    props: IProps,
): React.JSX.Element => {
    const v = props.value;
    const descriptions = v.description ?? [];

    return (
        <div
            className="l-full-width landing-page-description landing-page__section"
            style={backgroundImageStyle(v.image)}
        >
            <div className="l-capped-width landing-page-description__content">
                <div className="landing-page-description__copy">
                    {/* title/copy/d.title/d.description are CharBlock plain
                        text — render as text so React escaping matches Django
                        autoescape (NOT |safe). */}
                    <h2 className="landing-page-description__title landing-page__title">
                        {v.title}
                    </h2>
                    <div className="landing-page-description__desc landing-page__desc">
                        {v.copy}
                    </div>
                    <ul>
                        {descriptions.map((description, index) => (
                            <li
                                key={index}
                                className="landing-page-description__desc__block"
                            >
                                <div className="landing-page-description__desc__title">
                                    {description.title}
                                </div>
                                <div className="landing-page-description__desc__copy landing-page__desc">
                                    {description.description}
                                </div>
                            </li>
                        ))}
                    </ul>
                    {/* `shop_now_url` is the legacy
                        `{% slugurl 'tempur-vs-competitors' %}` resolved
                        server-side (request/site-relative CMS page URL); it is
                        `""` when the page can't be resolved, matching what
                        `slugurl` rendered for a missing page. */}
                    <a
                        className="landing-page-description__button button button--ming "
                        href={v.shop_now_url ?? ""}
                    >
                        <span className="landing-page-description__button-item">
                            shop now
                        </span>
                    </a>
                </div>
            </div>
        </div>
    );
};
